import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import { videos, categories } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

type TabType = '视频' | '资讯';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get('type') || 'all';
  const tabParam = (searchParams.get('tab') as TabType) || '视频';

  const [activeTab, setActiveTab] = useState<TabType>(tabParam);
  const [activeType, setActiveType] = useState(typeParam);
  const [loadedCount, setLoadedCount] = useState(8);

  useEffect(() => {
    const tab = searchParams.get('tab') as TabType;
    const type = searchParams.get('type') || 'all';
    if (tab) setActiveTab(tab);
    setActiveType(type);
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab, activeType]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setLoadedCount(8);
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params);
  };

  const handleTypeChange = (type: string) => {
    setActiveType(type);
    setLoadedCount(8);
    const params = new URLSearchParams(searchParams);
    if (type === 'all') {
      params.delete('type');
    } else {
      params.set('type', type);
    }
    setSearchParams(params);
  };

  const filteredVideos = useMemo(() => {
    if (activeType === 'all') return videos;
    return videos.filter((v) => v.category === activeType);
  }, [activeType]);

  const displayedVideos = filteredVideos.slice(0, loadedCount);
  const hasMore = loadedCount < filteredVideos.length;

  const currentCategory = categories.find((c) => c.id === activeType);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部'}${activeTab === '资讯' ? '资讯' : '视频'} - 91免费`,
    description: `浏览91免费的${currentCategory?.name || '全部'}${activeTab === '资讯' ? '资讯' : '视频'}内容，高清免费在线观看。`,
    url: `https://91free.com/category?type=${activeType}&tab=${activeTab}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91free.com' },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://91free.com/category' },
        { '@type': 'ListItem', position: 3, name: currentCategory?.name || '全部' },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类浏览' },
                  ...(currentCategory && activeType !== 'all' ? [{ label: currentCategory.name }] : []),
                ]}
              />
            </div>

            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                91免费在线视频
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                海量高清影视资源，免费在线观看
              </p>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
                <button
                  onClick={() => handleTabChange('视频')}
                  className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === '视频'
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className="ri-movie-line mr-1.5"></i>视频
                </button>
                <button
                  onClick={() => handleTabChange('资讯')}
                  className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === '资讯'
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className="ri-article-line mr-1.5"></i>资讯
                </button>
              </div>
            </div>

            {activeTab === '视频' && (
              <div className="mb-8 flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleTypeChange(cat.id)}
                    className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeType === cat.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}

            {activeTab === '视频' ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-8">
                  {displayedVideos.map((video) => (
                    <div key={video.id} data-product-shop>
                      <VideoCard video={video} />
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className="text-center pb-12">
                    <button
                      onClick={() => setLoadedCount((prev) => prev + 8)}
                      className="px-8 py-3 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      加载更多
                      <i className="ri-arrow-down-line ml-1.5"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-8">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="horizontal" />
                ))}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}